import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { useLabTestBooking } from '../hooks/useLabTestBooking';
import BookingStepper   from '../components/features/labtest/BookingStepper';
import OrderSummary     from '../components/features/labtest/OrderSummary';
import PatientSearchStep from '../components/features/labtest/PatientSearchStep';
import PlanCatalogStep  from '../components/features/labtest/PlanCatalogStep';
import AddressSlotStep  from '../components/features/labtest/AddressSlotStep';
import ReviewPayStep    from '../components/features/labtest/ReviewPayStep';
import { formatPrice }  from '../utils/labTestUtils';
import './LabTestFormPage.css';

import Icon from '../components/Icon';
const BREADCRUMBS = [
  { label: 'Lab Tests', to: '/lab-tests' },
  { label: 'Create Lab Test' },
];

export default function LabTestFormPage() {
  const navigate = useNavigate();
  const booking  = useLabTestBooking();

  const {
    selectedPatient, selectedSku, selectedSlot,
    orderPhase, orderAttempt, savedRzp, paidViaWallet,
    refundRequesting, refundResult,
    error,
    currentStep,
    handleProceedToPayment, handleRetry, handleRequestRefund,
    appointmentDate, address,
    razorpayEnabledForNavigator,
    walletBalanceMinorUnits, insufficientWalletBalance,
  } = booking;

  const pageSubtitle = !selectedPatient
    ? 'Search and select a patient to view available plans'
    : !selectedSku
      ? 'Choose a lab test plan from the catalog'
      : !selectedSlot
        ? 'Pick an appointment slot at the patient location'
        : "Review and charge the patient's wallet";

  return (
    <AppShell breadcrumbs={BREADCRUMBS}>
      <div className="lab-booking">
        <span className="back-link fade-in-up" onClick={() => navigate('/lab-tests')}>
          ← Back to Lab Tests
        </span>

        <div className="page-header fade-in-up">
          <div>
            <div className="page-title">Create Lab Test</div>
            <div className="page-subtitle">{pageSubtitle}</div>
          </div>
        </div>

        {/* 27 Jul 2026: new — see NavigatorPaymentConfig.java's Javadoc.
            Should never render today (property defaults false); if it's
            ever flipped without the Razorpay-enabled path being built, this
            is a much clearer failure mode than a confusing error after
            filling out the whole multi-step form. */}
        {razorpayEnabledForNavigator && (
          <div className="alert alert-error fade-in-up">
            <Icon name="alert" size={14} /> Razorpay-based navigator booking is enabled in configuration, but not yet built.
            Please contact engineering before attempting to book — wallet-only booking below
            may not behave as expected until this is resolved.
          </div>
        )}

        {error && <div className="alert alert-error fade-in-up"><Icon name="alert" size={14} /> {error}</div>}

        {/* Saving spinner */}
        {orderPhase === 'saving' && (
          <div className="alert fade-in-up" style={{
            background: 'var(--blue-bg)',
            border: '1.5px solid var(--brand-blue)',
            color: 'var(--brand-blue)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, borderColor: 'var(--brand-blue)', borderTopColor: 'transparent' }} />
            {orderAttempt === 0 ? 'Payment successful! Saving your order…' : `Retrying… (attempt ${orderAttempt + 1}/3)`}
          </div>
        )}

        {/* Refund section — shown after all 3 retries fail */}
        {orderPhase === 'refund' && (
          <div className="fade-in-up" style={{
            background: 'var(--bg-surface)',
            border: '2px solid var(--red)',
            borderRadius: 'var(--radius-md)',
            padding: '20px 24px',
            marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}><Icon name={paidViaWallet ? 'wallet' : 'card'} size={22} /></span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
                  {paidViaWallet ? 'Wallet charged — order save failed' : 'Payment received — order save failed'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                  {paidViaWallet
                    ? "The patient's wallet was debited, but the order could not be saved."
                    : savedRzp?.razorpay_payment_id
                      ? `Razorpay Payment ID: ${savedRzp.razorpay_payment_id}`
                      : 'Your payment was captured by Razorpay.'}
                </div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
              All save attempts failed. Click <strong>Request Refund</strong> to initiate a refund —
              you will be redirected once the request is submitted.
              <br />
              <strong style={{ color: 'var(--red)' }}>Do not navigate away before clicking the button below.</strong>
            </p>
            {refundResult && (
              <div className="alert alert-error" style={{ marginBottom: 12 }}>
                <Icon name="alert" size={14} /> {refundResult.message}
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRequestRefund}
              disabled={refundRequesting}
              style={{ background: 'var(--red)', borderColor: 'var(--red)', minWidth: 160 }}
            >
              {refundRequesting
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    Requesting…
                  </span>
                : '↩ Request Refund'}
            </button>
          </div>
        )}

        <BookingStepper currentStep={currentStep} />

        <form onSubmit={(e) => { e.preventDefault(); handleProceedToPayment(); }}>
          <div className="lab-booking-layout">
            <div className="lab-booking-main">

              {/* Step 1 — Patient */}
              <PatientSearchStep
                currentStep={currentStep}
                selectedPatient={selectedPatient}
                setSelectedPatient={booking.setSelectedPatient}
                patientQuery={booking.patientQuery}
                setPatientQuery={booking.setPatientQuery}
                patientResults={booking.patientResults}
                searchLoading={booking.searchLoading}
                showDropdown={booking.showDropdown}
                setShowDropdown={booking.setShowDropdown}
                searchRef={booking.searchRef}
                setError={booking.setError}
              />

              {/* Step 2 — Plan catalog */}
              {selectedPatient && (
                <PlanCatalogStep
                  currentStep={currentStep}
                  skuList={booking.skuList}
                  catalogLoading={booking.catalogLoading}
                  catalogError={booking.catalogError}
                  isLastPage={booking.isLastPage}
                  expandedSku={booking.expandedSku}
                  setExpandedSku={booking.setExpandedSku}
                  selectedSku={selectedSku}
                  setSelectedSku={booking.setSelectedSku}
                  fetchCatalog={booking.fetchCatalog}
                  handleLoadMore={booking.handleLoadMore}
                />
              )}

              {/* Step 3 — Address & slots */}
              {selectedPatient && selectedSku && (
                <AddressSlotStep
                  currentStep={currentStep}
                  selectedPatient={selectedPatient}
                  address={address}
                  patientAddresses={booking.patientAddresses}
                  addressesLoading={booking.addressesLoading}
                  addressMode={booking.addressMode}
                  selectedSavedAddressId={booking.selectedSavedAddressId}
                  newAddrForm={booking.newAddrForm}
                  savingPatientAddress={booking.savingPatientAddress}
                  addressFormMessage={booking.addressFormMessage}
                  selectSavedAddress={booking.selectSavedAddress}
                  switchToSavedAddresses={booking.switchToSavedAddresses}
                  switchToNewAddressForm={booking.switchToNewAddressForm}
                  setNewAddr={booking.setNewAddr}
                  handleSaveNewAddressToPatient={booking.handleSaveNewAddressToPatient}
                  appointmentDate={appointmentDate}
                  setAppointmentDate={booking.setAppointmentDate}
                  slots={booking.slots}
                  slotsLoading={booking.slotsLoading}
                  slotsError={booking.slotsError}
                  selectedSlot={selectedSlot}
                  setSelectedSlot={booking.setSelectedSlot}
                  handleSearchSlots={booking.handleSearchSlots}
                />
              )}

              {/* Step 4 — Review & pay */}
              {selectedPatient && selectedSku && selectedSlot && (
                <ReviewPayStep
                  selectedPatient={selectedPatient}
                  selectedSku={selectedSku}
                  selectedSlot={selectedSlot}
                  appointmentDate={appointmentDate}
                  address={address}
                  walletBalanceMinorUnits={walletBalanceMinorUnits}
                  insufficientWalletBalance={insufficientWalletBalance}
                />
              )}

            </div>

            <OrderSummary
              selectedPatient={selectedPatient}
              selectedSku={selectedSku}
              selectedSlot={selectedSlot}
              appointmentDate={appointmentDate}
              address={address}
              walletBalanceMinorUnits={walletBalanceMinorUnits}
              insufficientWalletBalance={insufficientWalletBalance}
            />
          </div>

          <div className="lab-booking-footer">
            <div className="form-actions fade-in-up delay-4">
              {orderPhase === 'retry' ? (
                <>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
                      Order save failed — attempt {orderAttempt}/3
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                      Payment was captured. {3 - orderAttempt} retry attempt{3 - orderAttempt === 1 ? '' : 's'} remaining.
                    </div>
                  </div>
                  <button type="button" className="btn btn-secondary btn-lg" onClick={handleRetry}>
                    ↺ Retry ({3 - orderAttempt} left)
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={() => navigate('/lab-tests')}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-primary btn-lg"
                    type="submit"
                    disabled={
                      orderPhase !== 'idle' || !selectedPatient || !selectedSku || !selectedSlot ||
                      insufficientWalletBalance
                    }
                  >
                    {orderPhase === 'paying'
                      ? 'Charging wallet…'
                      : orderPhase === 'saving'
                        ? 'Saving order…'
                        : orderPhase === 'refund'
                          ? 'Order failed — use refund above'
                          : insufficientWalletBalance
                            ? 'Insufficient Wallet Balance'
                            : selectedSku
                              ? `Charge ${formatPrice(selectedSku.rate)} to Patient Wallet`
                              : 'Charge Patient Wallet'}
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={() => navigate('/lab-tests')}>
                    Cancel
                  </button>
                  {/* 27 Jul 2026: new — insufficientWalletBalance check added
                      here too (Deepu's UX feedback, 27 Jul 2026), so this
                      helper text — which sits right next to the button,
                      unlike the review-step warning further up the page —
                      is the thing a navigator sees even if they never
                      scroll. */}
                  {(!selectedPatient || !selectedSku || !selectedSlot) ? (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
                      ℹ {!selectedPatient
                          ? 'Select a patient'
                          : !selectedSku
                            ? 'Select a plan'
                            : 'Select an appointment slot'} to continue
                    </div>
                  ) : insufficientWalletBalance ? (
                    <div style={{ fontSize: 12, color: 'var(--red)', alignSelf: 'center', fontWeight: 600 }}>
                      <Icon name="alert" size={14} /> Patient's wallet balance (₹{(walletBalanceMinorUnits / 100).toLocaleString('en-IN')}) is too low.
                      Ask them to top up first.
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
